import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { PR_CONFIG, PrConfig } from '../configuration/pr.config';
import { IDocumentExtendedRequest, IDocumentExtendedResponse } from '../models/document-extended';
import { IShareLinkRequest, IShareLinkResponse } from '../models/share-link';
import { IDocument } from '../models/documents';

@Injectable({
  providedIn: 'root',
})
export class ShareLinkApiService {
  private readonly apiUrl: string;
  constructor(
    // @ts-ignore
    @Inject(PR_CONFIG) private config: PrConfig,
    private readonly http: HttpClient,
  ) {
    this.apiUrl = this.config.apiUrl + 'share';
  }

  getShareLinkInfo(customerId: string, shareId: string): Observable<IShareLinkResponse> {
    return this.http.get<IShareLinkResponse>(`${this.apiUrl}/${customerId}/${shareId}`);
  }

  //-------- Authenticated User endpoints -------//
  getShareLink(shareLinkRequest: IShareLinkRequest): Observable<IShareLinkResponse> {
    return this.http.post<IShareLinkResponse>(`${this.apiUrl}`, shareLinkRequest);
  }

  getShareExtendedMetadata(
    shareId: string,
    documentExtendedRequest: IDocumentExtendedRequest,
  ): Observable<IDocumentExtendedResponse[]> {
    return this.http.post<IDocumentExtendedResponse[]>(
      `${this.apiUrl}/${shareId}/documents/metadatas/extended`,
      documentExtendedRequest,
    );
  }

  getSharedDocumentsMetadataByIds(
    shareId: string,
    documentIds: string[],
  ): Observable<IDocument[]> {
    return this.http.post<IDocument[]>(
      `${this.apiUrl}/${shareId}/documents/metadatasByIds`,
      documentIds,
    );
  }

  getSharedFileBlobByDocumentId(
    shareId: string,
    documentId: string,
    fileId: string,
    isDeleted: boolean = false,
    thumbnail: boolean = false,
    preview: boolean = false,
    currentLanguage: string = 'en',
  ): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${shareId}/documents/${documentId}/file/${fileId}`, {
      params: { isDeleted, thumbnail, preview, lang: currentLanguage },
      responseType: 'blob',
    });
  }

  getSharedDocumentsCount(shareId: string, filterOptions?: {}): Observable<number> {
    return this.http.get<number>(`${this.apiUrl}/${shareId}/documents/count`, filterOptions);
  }

  // ------- Endpoints for public share links anonymous user-------//
  getSharedExtendedMetadataPublic(
    customerId: string,
    shareId: string,
    documentExtendedRequest: IDocumentExtendedRequest,
  ): Observable<IDocumentExtendedResponse[]> {
    return this.http.post<IDocumentExtendedResponse[]>(
      `${this.apiUrl}/${customerId}/${shareId}/documents/metadatas/extended`,
      documentExtendedRequest,
    );
  }

  getSharedDocumentsMetadataByIdsPublic(
    customerId: string,
    shareId: string,
    documentIds: string[],
  ): Observable<IDocument[]> {
    return this.http.post<IDocument[]>(
      `${this.apiUrl}/${customerId}/${shareId}/documents/metadatasByIds`,
      documentIds,
    );
  }

  getSharedFileBlobByDocumentIdPublic(
    customerId: string,
    shareId: string,
    documentId: string,
    fileId: string,
    isDeleted: boolean = false,
    thumbnail: boolean = false,
    preview: boolean = false,
    currentLanguage: string = 'en',
  ): Observable<Blob> {
    return this.http.get(
      `${this.apiUrl}/${customerId}/${shareId}/documents/${documentId}/file/${fileId}`,
      {
        params: { isDeleted, thumbnail, preview, lang: currentLanguage },
        responseType: 'blob',
      },
    );
  }

  getSharedDocumentsCountPublic(
    customerId: string,
    shareId: string,
    filterOptions?: {},
  ): Observable<number> {
    return this.http.get<number>(
      `${this.apiUrl}/${customerId}/${shareId}/documents/count`,
      filterOptions,
    );
  }
}
